/**
 * Diagnostics polling step.
 *
 * Google's guidance: first poll 30 min after upload, then exponential
 * backoff (1.3x) capped at 60-min intervals, give up after 24 hours.
 *
 * Persistence: we localStorage-key by `requestId` so refreshing or
 * re-rendering within the same wizard session preserves the *uploadedAt*
 * timestamp (and therefore the polling clock keeps ticking accurately).
 * It does NOT power a "close the modal and rediscover later" flow — the
 * only entry point is via the `requestId` prop, which is held by the
 * parent wizard's state. A future "recent uploads" surface would need a
 * separate mechanism to enumerate persisted entries.
 *
 * Critically: we do NOT render an upload_match_rate_range band — that
 * field is on Customer Match audience-member statuses, NOT on
 * events_ingestion_status. For attributed counts, link to Google Ads
 * conversion reporting.
 */
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { offlineConversionsApi } from '@/services/api/offlineConversions';

interface Props {
  googleAdsAccountId: string;
  requestId: string;
}

const FIRST_POLL_DELAY_MS = 30 * 60 * 1000; // 30 minutes
const POLL_BACKOFF = 1.3;
const POLL_CAP_MS = 60 * 60 * 1000; // 60 minutes
const POLL_GIVE_UP_MS = 24 * 60 * 60 * 1000; // 24 hours

const STORAGE_KEY_PREFIX = 'offline-conversion-upload:';

interface PersistedState {
  requestId: string;
  uploadedAt: number; // epoch ms
}

function persist(requestId: string): void {
  const state: PersistedState = { requestId, uploadedAt: Date.now() };
  localStorage.setItem(STORAGE_KEY_PREFIX + requestId, JSON.stringify(state));
}

function loadPersisted(requestId: string): PersistedState | null {
  const raw = localStorage.getItem(STORAGE_KEY_PREFIX + requestId);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PersistedState;
  } catch {
    return null;
  }
}

function clearPersisted(requestId: string): void {
  localStorage.removeItem(STORAGE_KEY_PREFIX + requestId);
}

export const ResultsStep: React.FC<Props> = ({ googleAdsAccountId, requestId }) => {
  // Persist on first mount so the user can close + reopen.
  useEffect(() => {
    if (!loadPersisted(requestId)) persist(requestId);
  }, [requestId]);

  const persisted = loadPersisted(requestId);
  const uploadedAt = persisted?.uploadedAt ?? Date.now();
  const elapsed = Date.now() - uploadedAt;
  const givingUp = elapsed > POLL_GIVE_UP_MS;

  // Set a polling interval that scales — fast early so we catch quick
  // resolutions, then slowing toward Google's recommended cadence.
  const [pollIntervalMs, setPollIntervalMs] = useState<number>(
    elapsed < FIRST_POLL_DELAY_MS ? FIRST_POLL_DELAY_MS - elapsed : 5 * 60 * 1000
  );

  // Google's guidance: don't poll at all until 30 minutes after upload —
  // any earlier request will reliably come back PROCESSING with nothing
  // useful and just burn Data Manager API quota. Flip `firstPollReady` once
  // that window elapses; until then, useQuery stays disabled so it doesn't
  // fire on mount.
  const [firstPollReady, setFirstPollReady] = useState(elapsed >= FIRST_POLL_DELAY_MS);
  useEffect(() => {
    if (firstPollReady || givingUp) return;
    const remaining = FIRST_POLL_DELAY_MS - elapsed;
    const timer = setTimeout(() => setFirstPollReady(true), Math.max(0, remaining));
    return () => clearTimeout(timer);
    // `elapsed` derives from Date.now() at render time but we only need it
    // for the one-shot timer; intentionally exclude it from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPollReady, givingUp]);

  const query = useQuery({
    queryKey: ['offline-conversions-status', requestId],
    queryFn: () =>
      offlineConversionsApi.status({
        google_ads_account_id: googleAdsAccountId,
        request_id: requestId,
      }),
    refetchInterval: (q) => {
      // Stop polling once we've given up OR Google has returned a terminal
      // status. Each /status call hits Google's Data Manager API, so
      // continuing to poll after SUCCESS/FAILURE/PARTIAL_SUCCESS would just
      // waste quota and add load without changing what we'd display.
      if (givingUp) return false;
      const status = q.state.data?.request_status_per_destination[0]?.status;
      if (status && status !== 'PROCESSING') return false;
      return pollIntervalMs;
    },
    refetchOnWindowFocus: false,
    enabled: !givingUp && firstPollReady,
  });

  useEffect(() => {
    // Bail on the initial mount tick — `query.dataUpdatedAt` is 0 until the
    // first successful refetch, but useEffect always fires after mount. If
    // we apply the backoff here, the initial poll interval set by useState
    // gets multiplied before any request has happened, drifting away from
    // Google's recommended 30-min-then-backoff cadence.
    if (!query.dataUpdatedAt) return;
    const next = Math.min(pollIntervalMs * POLL_BACKOFF, POLL_CAP_MS);
    setPollIntervalMs(next);
    // Don't depend on pollIntervalMs to avoid an infinite re-render loop;
    // this runs once per successful query refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.dataUpdatedAt]);

  const data = query.data;
  const overallStatus = data?.request_status_per_destination[0]?.status ?? 'PROCESSING';
  const recordCount = data?.request_status_per_destination[0]?.record_count ?? 0;
  const warnings = data?.request_status_per_destination[0]?.warning_info ?? [];
  const errors = data?.request_status_per_destination[0]?.error_info ?? [];

  // Clean up the persisted entry once the polling loop is done — either
  // because Google has returned a terminal status (SUCCESS / FAILURE /
  // PARTIAL_SUCCESS) or because we've hit the 24h give-up wall. Without
  // this, localStorage grows unbounded across uploads (one entry per
  // requestId, never reclaimed).
  useEffect(() => {
    if (givingUp || (overallStatus && overallStatus !== 'PROCESSING')) {
      clearPersisted(requestId);
    }
  }, [givingUp, overallStatus, requestId]);

  return (
    <div className="space-y-4">
      <Alert variant="info">
        <p>
          <strong>Upload received.</strong> Request ID <code>{requestId}</code>.
        </p>
        <p className="mt-1 text-sm">
          Google&apos;s diagnostics typically resolve within a few hours, and
          attributed conversions appear in Google Ads reporting within{' '}
          <strong>24&ndash;48 hours</strong>. &ldquo;Processing&rdquo; below
          isn&apos;t a failure — it&apos;s just Google still matching.
        </p>
      </Alert>

      <Card className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Status:</span>
          <Badge>{overallStatus}</Badge>
        </div>
        <p className="text-sm text-gray-700">
          Records received by Google: <strong>{recordCount}</strong>
        </p>

        {warnings.length > 0 && (
          <div>
            <p className="font-medium text-yellow-700">Warnings</p>
            <ul className="ml-4 list-disc text-sm text-gray-700">
              {warnings.map((w, i) => (
                <li key={`${w.reason}-${i}`}>
                  {w.record_count} record{w.record_count === 1 ? '' : 's'}: {w.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {errors.length > 0 && (
          <div>
            <p className="font-medium text-red-700">Errors</p>
            <ul className="ml-4 list-disc text-sm text-gray-700">
              {errors.map((e, i) => (
                <li key={`${e.reason}-${i}`}>
                  {e.record_count} record{e.record_count === 1 ? '' : 's'}: {e.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-2 text-xs text-gray-500">
          For attributed conversion counts, check{' '}
          <em>Google Ads → Goals → Conversions</em> directly. Match-rate
          insight isn&apos;t available via diagnostics for events uploads.
        </p>
      </Card>

      {givingUp && (
        <Alert variant="warning">
          Stopped polling after 24 hours. If the upload still shows
          PROCESSING in Google Ads, contact Google Ads support.
        </Alert>
      )}
    </div>
  );
};
